
import { useState } from 'react';
import type { DataItem } from '../../admin/components/UploadSection';

interface DataCardProps {
  item: DataItem;
}

export default function DataCard({ item }: DataCardProps) {
  const [showTextModal, setShowTextModal] = useState(false);

  const getFileIcon = () => {
    if (item.type === 'text') {
      return { icon: 'ri-file-text-line', color: 'text-teal-600', bg: 'bg-teal-50' };
    }
    const fileType = item.fileType?.toLowerCase() || '';
    const fileName = item.fileName?.toLowerCase() || '';
    if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
      return { icon: 'ri-file-pdf-line', color: 'text-red-600', bg: 'bg-red-50' };
    }
    if (fileType.includes('word') || fileType.includes('document') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return { icon: 'ri-file-word-line', color: 'text-blue-600', bg: 'bg-blue-50' };
    }
    if (fileType.includes('excel') || fileType.includes('sheet') || fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
      return { icon: 'ri-file-excel-line', color: 'text-green-600', bg: 'bg-green-50' };
    }
    if (fileType.includes('image') || fileType.includes('png') || fileType.includes('jpg') || fileType.includes('jpeg') || fileType.includes('gif') || fileType.includes('webp')) {
      return { icon: 'ri-image-line', color: 'text-purple-600', bg: 'bg-purple-50' };
    }
    return { icon: 'ri-file-line', color: 'text-gray-600', bg: 'bg-gray-50' };
  };

  const handleDownload = () => {
    if (item.type === 'file' && item.fileUrl) {
      const link = document.createElement('a');
      link.href = item.fileUrl;
      link.download = item.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleView = () => {
    if (item.type === 'file' && item.fileUrl) {
      // For PDFs and images, open in new tab directly
      const fileType = item.fileType?.toLowerCase() || '';
      const fileName = item.fileName?.toLowerCase() || '';
      if (
        fileType.includes('pdf') || fileName.endsWith('.pdf') ||
        fileType.includes('image') || fileType.startsWith('image/')
      ) {
        window.open(item.fileUrl, '_blank');
      } else {
        // For other files, trigger download since browser can't preview them
        handleDownload();
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fileIconData = getFileIcon();
  // Support both uploadedAt (admin field) and createdAt (legacy)
  const displayDate = item.uploadedAt || (item as any).createdAt;

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 hover:border-teal-400 hover:shadow-sm transition-all overflow-hidden">
        {/* Icon Header */}
        <div className={`${fileIconData.bg} p-6 flex items-center justify-center`}>
          <div className="w-16 h-16 flex items-center justify-center">
            <i className={`${fileIconData.icon} text-5xl ${fileIconData.color}`}></i>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
              {item.title}
            </h3>
            {item.description && (
              <p className="text-sm text-gray-500 line-clamp-2">
                {item.description}
              </p>
            )}
          </div>

          {/* File Info */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-calendar-line"></i>
            <span>{formatDate(displayDate)}</span>
          </div>

          {item.fileName && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <i className="ri-file-line"></i>
              <span className="truncate">{item.fileName}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {item.type === 'file' ? (
              <>
                <button
                  onClick={handleView}
                  disabled={!item.fileUrl}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    item.fileUrl
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-eye-line text-base"></i>
                  View
                </button>
                <button
                  onClick={handleDownload}
                  disabled={!item.fileUrl}
                  className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    item.fileUrl
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                  }`}
                  title="Download"
                >
                  <i className="ri-download-line text-base"></i>
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowTextModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-base"></i>
                View Content
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Text Content Modal */}
      {showTextModal && item.type === 'text' && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTextModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg">
                  <i className="ri-file-text-line text-lg text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                  {item.description && (
                    <p className="text-xs text-gray-500">{item.description}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowTextModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {/* Modal Body */}
            <div className="p-5 overflow-y-auto flex-1">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                {item.textContent || 'No content available.'}
              </pre>
            </div>
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowTextModal(false)}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
